import {
  fetchCTAConfig,
  fetchDetailMenuSite,
  fetchListOfMenuSite,
  fetchMenuFeatures,
  fetchMetaAddressFormat,
  fetchScriptsApi,
  fetchStripeTokenApi,
  fetchTrackerApi,
  setMetaAddressFormatCache,
  setScriptsApiCache,
  setForStripeTokenCache,
  setCacheForTrackerCache,
  initializedOPWAction,
  fetchMaxDaysPickerConfig,
  fetchAnnouncementBarConfig
} from './action'
import { createAction, createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { isObjectEmpty } from 'src/utils/object'

interface MetaState {
  listOfMenuSite: PlainInfo<Meta.ListOfMenuSite>
  detailMenuSite: PlainInfo<Meta.MetaData>
  trackers: PlainInfo<Meta.Trackers[]>
  isBot: boolean
  scripts: PlainInfo<Meta.ShoppingCartTrackersData[]>
  cartName: string
  initializedOPW: boolean
}

type Nullable<T> = T | null

type PlainInfo<T> = {
  status: RequestState
  error?: unknown
  data: Nullable<T>
  mode?: string
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

const initialState: MetaState = {
  listOfMenuSite: {
    status: 'none',
    data: null,
    error: null
  },
  detailMenuSite: {
    status: 'none',
    data: null,
    error: null
  },
  isBot: false,
  trackers: {
    status: 'none',
    data: null,
    error: null
  },
  scripts: {
    status: 'none',
    data: [],
    error: null
  },
  cartName: '',
  initializedOPW: false
}

export type MetaStateInReducer = {
  '@meta': MetaState
}

const setBotMeta = createAction<{ isBot: boolean }>('set-bot-meta')

export const metaSlice = createSlice({
  name: '@meta',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchListOfMenuSite.pending, (state) => {
        state.listOfMenuSite.status = 'pending'
      })
      .addCase(fetchListOfMenuSite.fulfilled, (state, action) => {
        state.listOfMenuSite.status = 'fulfilled'
        state.listOfMenuSite.data = action.payload?.data
        state.cartName = action.meta.arg.slug
      })
      .addCase(fetchListOfMenuSite.rejected, (state, action) => {
        state.listOfMenuSite.status = 'rejected'
        state.listOfMenuSite.error = action.payload
      })
      .addCase(fetchDetailMenuSite.pending, (state) => {
        state.detailMenuSite.status = 'pending'
      })
      .addCase(fetchDetailMenuSite.fulfilled, (state, action) => {
        state.detailMenuSite.status = 'fulfilled'
        if (action.payload?.data) {
          const brandInfo = state.listOfMenuSite.data.menus.find(
            (menu) => menu.id === action.payload.data.menu.id
          )
          state.detailMenuSite.data = {
            ...action.payload.data,
            ...(brandInfo?.brand
              ? {
                  brand: brandInfo.brand,
                  account: brandInfo.account
                }
              : {})
          }
        }
        state.detailMenuSite.mode = action.payload?.mode
      })
      .addCase(fetchDetailMenuSite.rejected, (state, action) => {
        state.detailMenuSite.status = 'rejected'
        state.detailMenuSite.error = action.payload
        state.detailMenuSite.mode = (action.payload as { mode: string })?.mode
      })
      .addCase(setBotMeta, (state, action) => {
        state.isBot = action.payload.isBot
      })
      .addCase(fetchMenuFeatures.pending, (state) => {
        state.detailMenuSite.data = { ...state.detailMenuSite.data }
      })
      .addCase(fetchMenuFeatures.fulfilled, (state, action) => {
        state.detailMenuSite.data = {
          ...state.detailMenuSite.data,
          features: action.payload?.data
        }
      })
      .addCase(fetchMenuFeatures.rejected, (state, action) => {
        state.detailMenuSite.status = 'rejected'
        state.detailMenuSite.data = { ...state.detailMenuSite.data }
        state.detailMenuSite.error = action.payload
      })
      .addCase(fetchMetaAddressFormat.pending, (state) => {
        state.detailMenuSite.data = { ...state.detailMenuSite.data }
      })
      .addCase(fetchMetaAddressFormat.fulfilled, (state, action) => {
        state.detailMenuSite.data = {
          ...state.detailMenuSite.data,
          checkoutAddressFormat: action.payload?.data
        }
      })
      .addCase(fetchMetaAddressFormat.rejected, (state) => {
        state.detailMenuSite.data = { ...state.detailMenuSite.data }
      })
      .addCase(fetchTrackerApi.pending, (state) => {
        state.trackers.status = 'pending'
      })
      .addCase(fetchTrackerApi.fulfilled, (state, action) => {
        state.trackers.status = 'fulfilled'
        state.trackers.data = action.payload?.data
      })
      .addCase(fetchTrackerApi.rejected, (state, action) => {
        state.trackers.status = 'rejected'
        state.trackers.error = action.error.message
      })
      .addCase(fetchScriptsApi.pending, (state) => {
        state.scripts.status = 'pending'
      })
      .addCase(fetchScriptsApi.fulfilled, (state, action) => {
        state.scripts.status = 'fulfilled'
        if (action.payload?.payload?.shoppingCartTrackers) {
          state.scripts.data =
            action.payload.payload.shoppingCartTrackers.filter(
              (script) => script.enabled
            )
        }
      })
      .addCase(fetchScriptsApi.rejected, (state, action) => {
        state.scripts.status = 'rejected'
        state.scripts.error = action.error.message
      })
      .addCase(fetchStripeTokenApi.pending, (state) => {
        state.detailMenuSite.data.stripeToken = null
      })
      .addCase(fetchStripeTokenApi.fulfilled, (state, action) => {
        state.detailMenuSite.data.stripeToken = action.payload?.data.stripeToken
      })
      .addCase(fetchStripeTokenApi.rejected, (state) => {
        state.detailMenuSite.data.stripeToken = null
      })
      .addCase(initializedOPWAction, (state) => {
        state.initializedOPW = true
      })
      .addCase(setMetaAddressFormatCache, (state, action) => {
        state.detailMenuSite.data = {
          ...state.detailMenuSite.data,
          checkoutAddressFormat: action.payload
        }
      })
      .addCase(setForStripeTokenCache, (state, action) => {
        state.detailMenuSite.data.stripeToken = action.payload
      })
      .addCase(setCacheForTrackerCache, (state, action) => {
        state.trackers.status = action.payload.status
        state.trackers.data = action.payload.data
      })
      .addCase(setScriptsApiCache, (state, action) => {
        state.scripts.status = action.payload.status
        state.scripts.data = action.payload.data
      })
      .addCase(fetchCTAConfig.fulfilled, (state, action) => {
        if (!isObjectEmpty(state.listOfMenuSite.data)) {
          state.listOfMenuSite.data.ctaConfig = action.payload
        }
      })
      .addCase(fetchMaxDaysPickerConfig.fulfilled, (state, action) => {
        state.detailMenuSite.data.maxDaysPickerConfig =
          state.detailMenuSite.data.countryCode === 'MY' ? 50 : 80
        if (
          typeof action.payload === 'number' &&
          !state.detailMenuSite.data.features.enableLimitAdvancedOrders
        ) {
          state.detailMenuSite.data.maxDaysPickerConfig = action.payload
        }
      })
      .addCase(fetchAnnouncementBarConfig.fulfilled, (state, action) => {
        if (action.payload) {
          state.detailMenuSite.data.announcementBar = action.payload
        }
      })
      .addCase(HYDRATE, (state, action) => ({
        ...state,
        ...action['payload']['@meta']
      }))
})
